import { Component, OnInit, QueryList, ViewChildren } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { Value } from "../../models/value";
import { MagicBoxService } from "../../services/magic-box.service";
import { ListSelectorComponent } from "../../components/list-selector/list-selector.component";
import {
  FiltersMagicBoxActions,
  FiltersMagicBoxStateInterface,
} from "../../store/actions/filters-magic-box.actions";
import { Store } from "@ngrx/store";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { AdminService } from "../../services/admin.service";
import { FiltersService } from "../../services/filters.service";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { FeatureCountries } from "../../models/featureCountries";
import { User } from "../../models/user";
import { Subscription } from "rxjs";
import { SidenavService } from "../../services/sidenav.service";

@Component({
  selector: "app-magic-box",
  templateUrl: "./magic-box.component.html",
  styleUrls: ["./magic-box.component.scss"],
})
export class MagicBoxComponent implements OnInit {
  referentialTypes: Value[];
  destinations: Value[];
  filters: FiltersMagicBoxStateInterface;
  loading: boolean;
  fields: Value[];
  editMode: boolean;
  canScheduleMagicBox: Boolean;
  forceInter: boolean;
  applyTo: string[];
  applyToSelector: string[];
  canEdit: string[];
  id: number;
  disableSearch = false;
  isNonSportiveClassif = false;
  subscriptions: Subscription[];
  currentUser: User;
  disabledEditing: boolean = false;
  features: FeatureCountries[];
  languageId: number;
  initSearch: boolean = true;

  @ViewChildren("selector") selectors: QueryList<ListSelectorComponent>;

  /**
   * Initialize variables
   * @param {Title} title
   * @param {MagicBoxService} magicBoxService
   * @param {FiltersMagicBoxActions} filtersMagicBoxActions
   * @param {Router} router
   * @param {AdminService} adminService
   * @param {FiltersService} filtersService
   * @param {Store<any>} store
   */
  constructor(
    private activatedRoute: ActivatedRoute,
    private title: Title,
    private magicBoxService: MagicBoxService,
    private filtersMagicBoxActions: FiltersMagicBoxActions,
    private router: Router,
    private adminService: AdminService,
    private filtersService: FiltersService,
    private store: Store<any>,
    private sidenavService: SidenavService
  ) {
    this.title.setTitle("Taiga - Magic Box");
    this.sidenavService.currentTab = "Magic Box";
    this.loading = true;
    this.forceInter = false;
    this.applyTo = [];
    this.subscriptions = [];
    this.canEdit = [];
    this.applyToSelector = [];
    this.isNonSportiveClassif = false;
    this.disabledEditing = false;
    this.getUserState();
  }

  /**
   * Get the current user from the store
   */
  getUserState() {
    this.subscriptions.push(
      this.store.select("userState").subscribe((user: User) => {
        if (user && user.currentLanguage) {
          this.currentUser = user;
        }
      })
    );
  }

  /**
   * Get filters from the store
   */
  ngOnInit() {
    /**
     * Check the user rights for this component
     */
    this.adminService.init().subscribe(() => {
      this.editMode =
        this.adminService.can("Magic Box Digital", "Edit") ||
        this.adminService.can("Magic Box Press", "Edit") ||
        this.adminService.can("Magic Box Popup Next", "Edit") ||
        this.adminService.can("Magic Box Partners", "Edit");

      this.canScheduleMagicBox =
        this.adminService.can("Magic Box Digital", "View") ||
        this.adminService.can("Magic Box Press", "View") ||
        this.adminService.can("Magic Box Popup Next", "View") ||
        this.adminService.can("Magic Box Partners", "View");

      if (this.adminService.can("Magic Box Digital", "View")) {
        this.applyTo.push("Digital");
      }

      if (this.adminService.can("Magic Box Press", "View")) {
        this.applyTo.push("Press");
      }

      if (this.adminService.can("Magic Box Popup Next", "View")) {
        this.applyTo.push("Popup Next");
      }

      if (this.adminService.can("Magic Box Partners", "View")) {
        this.applyTo.push("Partners");
      }
      this.store
        .select("filtersMagicBoxState")
        .subscribe((filters: FiltersMagicBoxStateInterface) => {
          this.filters = Object.assign({}, filters);
          if (!this.filters.applyTo || this.filters.applyTo.length === 0) {
            this.filters.applyTo = this.applyTo;
          }
          this.applyToSelector = this.filters.applyTo;
          this.filtersService.clear();
          this.loadData();
        });
    });
    this.EditMagicBoxByLanguage();
  }

  // Check MagicBox Edit Authorization by language
  EditMagicBoxByLanguage() {
    this.disabledEditing =
      this.currentUser.currentLanguage.features.includes("DATA_EDIT_MAGICBOX");
  }

  /**
   * Get the filters selectable choices from the API
   */
  loadData() {
    this.magicBoxService
      .getReferentialTypes(this.isNonSportiveClassif)
      .subscribe((types) => {
        this.referentialTypes = types;
        types.forEach((type) => {
          if (this.filters[type.value]) {
            this.filtersService.addEntity(this.filters[type.value]);
          }
        });
        this.loading = false;
      });

    this.destinations = JSON.parse(localStorage.getItem("destinations")).filter(
      (destination) => destination.id != "AllDestinations"
    );

    this.magicBoxService.getFields().subscribe((fields) => {
      this.fields = fields;

      this.activatedRoute.params.subscribe((params: Params) => {
        if (params.id) {
          this.filters.id = params.id;
          this.filters.showDeleted = params.showDeleted === "true";
          // Get FieldId
          this.filters.for = this.fields.find(
            (f) => f.value.toLowerCase() === params.field.toLowerCase()
          ).id;
          if (this.initSearch) {
            this.searchRules(true);
          }
        }
      });
    });
  }

  /**
   * Clear the selectors
   */
  resetSelectors() {
    this.filtersService.clear();
    this.selectors.forEach((selector) => selector.clearSelection());
  }

  /**
   * Set filters from the selectors
   */
  searchRules(fromInit: boolean = false) {
    if (fromInit) {
      this.initSearch = false;
    }
    this.disableSearch = true;
    this.selectors.forEach((selector) => {
      this.filters[selector.type.value] = selector.selectedValues[0];
    });
    this.filters.applyTo = this.applyToSelector;
    // insert sport/non sport filter
    this.filters.isNonSportiveClassif = this.isNonSportiveClassif;
    this.filtersMagicBoxActions.setFilters(this.filters);
    // Petit hack pour éviter d'avoir des doublons après un double clique
    setTimeout(() => {
      this.disableSearch = false;
    }, 500);
  }

  /**
   * Get the selector value
   * @param type
   * @returns {any}
   */
  getFiltersSelectorValue(type) {
    const selected = this.filters[type.value];
    if (selected) {
      return [selected];
    } else {
      return [];
    }
  }

  /**
   * Get filters from selector and store data
   */
  getFilters() {
    this.magicBoxService.creationRuleFilters = {};
    this.selectors.forEach((selector) => {
      this.magicBoxService.creationRuleFilters[selector.type.value] =
        selector.selectedValues;
    });
    this.magicBoxService.creationRuleFilters["for"] = this.filters.for;
    this.magicBoxService.creationRuleFilters["applyTo"] = this.applyToSelector;
    this.magicBoxService.creationRuleFilters["isNonSportiveClassif"] =
      this.isNonSportiveClassif;
  }

  /**
   * Open the new rule modal
   */
  createRule() {
    this.getFilters();
    this.router.navigate([{ outlets: { modal: ["new", "rule"] } }]);
  }

  /**
   * Open the new schedule modal
   */
  createSchedule() {
    this.router.navigate([{ outlets: { modal: ["new", "schedule"] } }]);
  }

  /**
   * When force inter is changed, clear the selectors to avoid invalid data
   */
  forceInterChanged() {
    this.selectors.forEach((selector) => selector.clearSelection());
  }

  applyToChange() {
    this.filters.applyTo = this.applyToSelector;
  }

  /**
   * Change Between Sportive/NonSportive Classification and clear the selectors to avoid invalid data
   */
  changeTypeClassification(event) {
    this.isNonSportiveClassif = event.checked;
    this.selectors.forEach((selector) => selector.clearSelection());
    this.magicBoxService
      .getReferentialTypes(this.isNonSportiveClassif)
      .subscribe((types) => {
        this.referentialTypes = types;
        types.forEach((type) => {
          if (this.filters[type.value]) {
            this.filtersService.addEntity(this.filters[type.value]);
          }
        });
      });
  }

  /**
   * Fetch the extract file and generate an url for the user
   * @param {string} format
   */
  extract() {
    this.disableSearch = true;
    this.selectors.forEach((selector) => {
      this.filters[selector.type.value] = selector.selectedValues[0];
    });
    this.magicBoxService.getExtract(this.filters).subscribe((res) => {
      this.exportAsExcelFile(res, "MAGIC_BOX_RULES");
      this.disableSearch = false;
    });
  }

  exportAsExcelFile(json: any[], excelFileName: string): void {
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json);
    const workbook: XLSX.WorkBook = {
      Sheets: { data: worksheet },
      SheetNames: ["data"],
    };
    const excelBuffer: any = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    this.saveAsExcelFile(excelBuffer, excelFileName);
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    FileSaver.saveAs(
      data,
      fileName + "_export_" + new Date().getDate() + ".xlsx"
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/magic-box/magic-box.component.ts